import React, { useRef, useState, useCallback } from 'react';
import { Modal } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { useLabels, useRequestData } from 'wsm-common-data';
import { useSelector } from 'react-redux';
import { galleryTrackingEvents } from '../../utilities/tracking';

import ImageSlider from '../common/ImageSlider';
import GalleryButton from './GalleryButton';
import useMedia from '../../hooks/useMedia';
import useHorizontalLayout from '../../hooks/useHorizontalLayout';
import Thumbnails from '../thumbnails/Thumbnails';
import DDCPlayerVideoComponent from '../DDCPlayerVideo/DDCPlayerVideoComponent';
import VehicleImageCount from '../common/VehicleImageCount';

const GalleryModal = ({
	openModal,
	onClose,
	carouselIndex,
	setCarouselIndex,
	activeToolBar,
	setActiveToolbar,
	imgTitle
}) => {
	const { videos = [], imagesToDisplay } = useSelector(
		(state) => state.media
	);
	const initialFocusRef = useRef(null);
	const modalSliderRef = useRef(null);
	const [modalWrapperElement, setModalWrapperElement] = useState(null);
	const modalWrapperRef = useCallback((node) => {
		if (node !== null) {
			setModalWrapperElement(node);
		}
	}, []);
	const labels = useLabels();
	const { widgetName, windowId } = useRequestData();
	const isDesktop = useMedia('(min-width: 576px)', true);
	const isHorizontalLayout = useHorizontalLayout();

	const isMobile = !isDesktop;
	const isTextLinkMode = true;
	const { widgetInfo } = useSelector((state) => state);
	const hasVideo = videos.length > 0;
	const TOTAL_IMAGES = imagesToDisplay.length;

	const btnSpace = 'mx-0';
	const supportARIA = (modal) => {
		const modalWrap = modal.closest('[aria-hidden]');
		if (modalWrap) {
			modalWrap.removeAttribute('aria-hidden');
		}
	};

	const handleOpenModal = () => {
		const modal = document.querySelector('#ws-vehicle-gallery-modal');
		if (modal) {
			supportARIA(modal);
			initialFocusRef?.current?.focus();
			const slickArrows = document.querySelectorAll(
				'.slick-arrow.slick-next, slick-arrow.slick-prev'
			);
			slickArrows.forEach((arrow) => {
				arrow.setAttribute('tabindex', '-1');
				arrow.setAttribute('aria-hidden', 'true');
			});
		}
		// For the ws-vehicle-toolbar portal inside the modal,
		// enable the mobile slidein for the toolbar CTA
		if (window?.DDC?.client?.isMobile) {
			window.DDC.mobile?.slidein?.init(
				'.vehicle-gallery-toolbar-react-portal .dialog'
			);
		}
	};

	return (
		<Modal
			show={openModal}
			onHide={onClose}
			onEntered={handleOpenModal}
			backdrop="static"
			id="ws-vehicle-gallery-modal"
			className={isMobile ? 'isMobile w-100 border-0' : ''}
			aria-modal="true"
			aria-label={labels.get('IMAGE_GALLERY')}
		>
			<div className="border-bottom-0 text-body">
				<div className="border-bottom">
					<div className="container-max-md px-sm-4 px-lg-8 py-3">
						<div className="vehicle-gallery-toolbar-react-portal">
							<button
								type="button"
								aria-label={labels.get('CLOSE')}
								className="float-right btn-unstyled text-link-muted mt-5 ml-3 p-0 border-bottom-0"
								onClick={onClose}
								ref={initialFocusRef}
							>
								<i
									className="ddc-icon ddc-icon-remove2"
									aria-hidden="true"
								/>
							</button>
						</div>
					</div>
				</div>
				<div
					className={`mt-5 mt-lg-6 ${
						!isMobile ? 'container-max-md px-lg-6 px-xl-8' : ''
					} ${!isHorizontalLayout ? 'mt-xl-8' : ''}`}
				>
					<div
						className={`modal-content-gallery ${
							isHorizontalLayout
								? 'layout-horizontal'
								: 'layout-vertical'
						}`}
					>
						<div className="modal-content-gallery-menu mb-3 mb-lg-0 pl-4 pl-md-2 pl-lg-4 pl-xl-0">
							{hasVideo && (
								<div className="d-flex flex-row flex-md-column">
									<div className="mb-md-5 mr-5 mr-md-0">
										<GalleryButton
											isTextLinkMode={isTextLinkMode}
											icon="ddc-icon-images2"
											btnLabel={labels.get('PHOTOS')}
											btnSpace={btnSpace}
											isGalleryModal
											activeToolBar={activeToolBar}
											value={0}
											handleClick={() => {
												galleryTrackingEvents.clickPhotosTab(
													widgetName,
													windowId
												);
												setActiveToolbar(0);
											}}
										/>
									</div>
									<div>
										<GalleryButton
											isTextLinkMode={isTextLinkMode}
											icon="ddc-icon-play3"
											btnLabel={labels.get('VIDEO')}
											btnSpace={btnSpace}
											isGalleryModal
											activeToolBar={activeToolBar}
											value={1}
											handleClick={() => {
												galleryTrackingEvents.clickVideoTab(
													widgetName,
													windowId
												);
												setActiveToolbar(1);
											}}
										/>
									</div>
								</div>
							)}
						</div>
						<div
							className="modal-content-gallery-slider"
							ref={modalWrapperRef}
						>
							{activeToolBar === 1 ? (
								<DDCPlayerVideoComponent
									video={videos[0]}
									widgetInfo={widgetInfo}
								/>
							) : (
								<ImageSlider
									openModal={openModal}
									carouselIndex={carouselIndex}
									setCarouselIndex={setCarouselIndex}
									type="modal"
									sliderRef={modalSliderRef}
									widgetElement={modalWrapperElement}
									imgTitle={imgTitle}
								/>
							)}
						</div>

						{activeToolBar === 0 ? (
							<div
								className="modal-content-gallery-thumbnails"
								id="gallery-modal-thumbnail-wrapper"
							>
								{isMobile ? (
									<div className="mb-3 mt-n3">
										<VehicleImageCount
											className="btn btn-unstyled pointer-events-none translate-left-50 vehicle-image-count mb-3 mt-n3"
											openModal={openModal}
											mediaCount={TOTAL_IMAGES}
											imageIndex={carouselIndex + 1}
										/>
									</div>
								) : (
									''
								)}
								<Thumbnails
									carouselIndex={carouselIndex}
									setCarouselIndex={setCarouselIndex}
									isMobile={isMobile}
									key={isMobile}
									modalSliderRef={modalSliderRef}
									imgTitle={imgTitle}
									isHorizontalLayout={isHorizontalLayout}
								/>
							</div>
						) : (
							<></>
						)}
					</div>
				</div>
			</div>
		</Modal>
	);
};

GalleryModal.propTypes = {
	onClose: PropTypes.func.isRequired,
	openModal: PropTypes.bool.isRequired,
	carouselIndex: PropTypes.number.isRequired,
	setCarouselIndex: PropTypes.func.isRequired,
	activeToolBar: PropTypes.number.isRequired,
	setActiveToolbar: PropTypes.func.isRequired,
	imgTitle: PropTypes.string
};

export default GalleryModal;
